import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogHeader, AlertDialogOverlay, Button, Center, Heading, VisuallyHiddenInput, useDisclosure } from "@chakra-ui/react";
import { faCancel, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, Dispatch, SetStateAction } from "react";
import VideoRenderer from "./videoRenderer";
import ImageChecker from "./imageChecker";
import PdfChecker from "./pdfChecker";
import AudioChecker from "./audioChecker";
import { MediaMeta } from "@/pages/mediaManager";

export type RenderJob = {
	trim: { start: number, end: number },
	cropBox: { x: number, y: number, width: number, height: number },
	newMedia: MediaMeta
	mode?: 'contain' | 'cover'
}

const AddMediaButton: React.FC<{
	onAdd: (newMedia: MediaMeta, file: File, icon?: File) => void
	onValidate: (newMedia: MediaMeta) => string | undefined
	editQueue: File[]
	setEditQueue: Dispatch<SetStateAction<File[]>>
}> = ({ onAdd, onValidate, editQueue, setEditQueue }) => {
	const { isOpen, onOpen, onClose } = useDisclosure()

	function onAddWrapper(newMedia: MediaMeta, file: File, icon?: File) {
		onAdd(newMedia, file, icon)
		if (editQueue.length) setEditQueue(editQueue => editQueue.slice(1))
	}

	const hiddenFileInput = useRef<HTMLInputElement>(null)
	const closeDialogRef = useRef(null)



	useEffect(() => {
		if (editQueue.length) onOpen()
	}, [editQueue])

	return (
		<>
			<VisuallyHiddenInput
				type="file"
				ref={hiddenFileInput}
				onChange={(e) => setEditQueue([...editQueue, ...e.target.files!])}
				multiple
				accept='image/*, video/*, audio/*, .pdf'
			/>
			{editQueue.length ? (
				<Button variant='inline' onClick={onOpen}>Resume Uploads</Button>
			) : (
				<Button variant='outline' size="sm" colorScheme="blue" onClick={() => hiddenFileInput.current!.click()} leftIcon={<FontAwesomeIcon icon={faPlus} />}>Add Media</Button>
			)}
			{/** Popup that handles file uploads */}
			<AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={closeDialogRef}>
				<AlertDialogOverlay>
					<AlertDialogContent maxW='90vw'>
						<AlertDialogHeader>
							<Center flexDir='column' gap={5}>
								<Heading size='md'>Media Uploader</Heading>
							</Center>
						</AlertDialogHeader>
						<AlertDialogBody>
							<VideoRenderer
								video={editQueue.length !== 0 ? editQueue[0] : undefined}
								onValidate={onValidate}
								onSubmit={() => setEditQueue(editQueue.slice(1))}
								onUpload={onAdd}
								onCloseDialouge={onClose}
							/>
							{editQueue.length && editQueue[0].type.startsWith('image') ? (
								<ImageChecker
									onAdd={onAddWrapper}
									currentImage={editQueue[0]}
									onValidate={onValidate}
								/>) : <></>
							}
							{editQueue.length && editQueue[0].type.endsWith('pdf') ? (
								<PdfChecker
									currentDocument={editQueue[0]}
									onValidate={onValidate}
									onAdd={onAddWrapper} />) : <></>}
							{editQueue.length && editQueue[0].type.startsWith('audio') ? (
								<AudioChecker
									currentAudio={editQueue[0]}
									onValidate={onValidate}
									onAdd={onAddWrapper} />) : <></>}
							{editQueue.length ? (
								<Center gap={3} paddingBottom={3}>
									<Button flex={1} leftIcon={<FontAwesomeIcon icon={faCancel} />} onClick={() => {
										setEditQueue([])
									}}>Cancel Uploads</Button>
									<Button flex={1} leftIcon={<FontAwesomeIcon icon={faTrash} />} onClick={() => setEditQueue(editQueue.slice(1))}>Remove Current</Button>
								</Center>
							) : <></>}
						</AlertDialogBody>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	)
}

export default AddMediaButton
